/**
 * RS / Filters — full-width panel over the native facet form.
 *
 * The form and its inputs carry Shopify's own facet params (param_name /
 * value from Search & Discovery), so applying is a plain GET submit to the
 * collection URL — cumulative, native, pagination-coherent. This element only
 * handles presentation: panel toggle (Esc / click outside), the pending-
 * selection note, and the custom sort dropdown (a listbox over the native
 * sort_by parameter, applied via hidden input + form submit).
 */
/* The landing: no facet in the URL and no sort other than the default. Also
   the rule's reset point: once the filters are emptied, the next filter is
   a first filter again */
const isCleanLanding = (defaultSort) => {
  const params = new URLSearchParams(location.search);
  if ([...params.keys()].some((key) => key.startsWith('filter.'))) return false;
  const sort = params.get('sort_by');
  return sort === null || sort === defaultSort;
};

class RsFilters extends HTMLElement {
  #open = false;
  #sortOpen = false;

  connectedCallback() {
    this.toggleBtn = this.querySelector('[data-rs-toggle]');
    this.panel = this.querySelector('[data-rs-panel]');
    this.form = this.querySelector('[data-rs-form]');
    this.countNote = this.querySelector('[data-rs-count]');
    if (!this.toggleBtn || !this.panel || !this.form) return;

    this.defaultCountText = this.countNote ? this.countNote.textContent : '';

    this.toggleBtn.addEventListener('click', () => (this.#open ? this.#close() : this.#openPanel()));

    this.onDocClick = (event) => {
      if (this.#sortOpen && !this.sortWrap.contains(event.target)) this.#closeSort();
      if (!this.#open) return;
      if (this.panel.contains(event.target) || this.toggleBtn.contains(event.target)) return;
      this.#close();
    };
    document.addEventListener('click', this.onDocClick);

    this.onKeydown = (event) => {
      if (event.key !== 'Escape') return;
      if (this.#sortOpen) {
        event.preventDefault();
        this.#closeSort();
        this.sortTrigger.focus();
        return;
      }
      if (this.#open) {
        event.preventDefault();
        this.#close();
        this.toggleBtn.focus();
      }
    };
    document.addEventListener('keydown', this.onKeydown);

    this.form.addEventListener('change', () => this.#updateCount());

    this.#setupSort();
    this.#setupAutoAvailability();
    this.#setupVendorAvailability();
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocClick);
    document.removeEventListener('keydown', this.onKeydown);
  }

  /* Shared state: only one surface open at a time */
  #openPanel() {
    if (this.#sortOpen) this.#closeSort();
    this.#open = true;
    this.panel.hidden = false;
    this.toggleBtn.setAttribute('aria-expanded', 'true');
  }

  #close() {
    this.#open = false;
    this.panel.hidden = true;
    this.toggleBtn.setAttribute('aria-expanded', 'false');
  }

  #updateCount() {
    if (!this.countNote) return;
    const checked = this.form.querySelectorAll('input[type="checkbox"]:checked').length;
    this.countNote.textContent = checked > 0 ? `${checked} selected` : this.defaultCountText;
  }

  /* Custom sort dropdown: listbox semantics, roving focus, native sort_by */
  #setupSort() {
    this.sortWrap = this.querySelector('[data-rs-sort-wrap]');
    if (!this.sortWrap) return;
    this.sortTrigger = this.sortWrap.querySelector('[data-rs-sort-trigger]');
    this.sortPanel = this.sortWrap.querySelector('[data-rs-sort-panel]');
    this.sortInput = this.sortWrap.querySelector('[data-rs-sort-input]');
    this.sortOptions = Array.from(this.sortWrap.querySelectorAll('[data-rs-sort-option]'));
    if (!this.sortTrigger || !this.sortPanel || !this.sortInput) return;

    this.sortTrigger.addEventListener('click', () => (this.#sortOpen ? this.#closeSort() : this.#openSort()));

    this.sortTrigger.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        if (!this.#sortOpen) this.#openSort();
      }
    });

    this.sortPanel.addEventListener('keydown', (event) => {
      const index = this.sortOptions.indexOf(document.activeElement);
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        const next = this.sortOptions[Math.min(index + 1, this.sortOptions.length - 1)];
        next?.focus();
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        const prev = this.sortOptions[Math.max(index - 1, 0)];
        prev?.focus();
      } else if (event.key === 'Tab') {
        this.#closeSort();
      }
    });

    for (const option of this.sortOptions) {
      option.addEventListener('click', () => {
        this.sortInput.value = option.dataset.value;
        this.#closeSort();
        this.form.requestSubmit();
      });
    }
  }

  #openSort() {
    if (this.#open) this.#close();
    this.#sortOpen = true;
    this.sortPanel.hidden = false;
    this.sortTrigger.setAttribute('aria-expanded', 'true');
    const active = this.sortOptions.find((o) => o.classList.contains('is-active')) || this.sortOptions[0];
    active?.focus();
  }

  #closeSort() {
    this.#sortOpen = false;
    this.sortPanel.hidden = true;
    this.sortTrigger.setAttribute('aria-expanded', 'false');
  }

  /* /collections/all only (Liquid gates the attribute). Stateless rule, the
     URL is the only memory — nothing sticks across tabs, restores or days:
     the automatism fires on the FIRST filter or sort applied from the clean
     landing (no filter, no sort in the URL). IN STOCK is checked for real
     before the GET submit, so it lands in the URL and facet counts, the
     panel state and shared links all stay truthful. From any filtered state
     nothing is ever added: the URL already carries IN STOCK if the
     automatism (or the user) put it there, and if the user removed it by
     hand it stays removed until the filters are emptied again. A bare APPLY
     with nothing selected is not a filter. The rails apply the very same
     rule (rs-marquee-menu.js). */
  #setupAutoAvailability() {
    if (!this.hasAttribute('data-rs-auto-availability')) return;
    const name = this.getAttribute('data-rs-avail-name');
    const inStockValue = this.getAttribute('data-rs-avail-instock');
    const defaultSort = this.getAttribute('data-rs-default-sort');
    const availInputs = Array.from(this.form.querySelectorAll(`input[name="${CSS.escape(name)}"]`));
    const inStockInput = availInputs.find((input) => input.value === inStockValue);
    if (!inStockInput) return;

    // Back/forward restores the bfcache DOM, where the automatism may have
    // left IN STOCK checked pre-navigation: resync the inputs to the URL,
    // which is the only truth
    window.addEventListener('pageshow', (event) => {
      if (!event.persisted) return;
      const active = new URLSearchParams(location.search).getAll(name);
      for (const input of availInputs) input.checked = active.includes(String(input.value));
    });

    this.form.addEventListener('submit', () => {
      // The default sort is not a sort: keep it out of the URL, so the rails
      // and the panel produce the same clean URLs
      if (this.sortInput && defaultSort) this.sortInput.disabled = this.sortInput.value === defaultSort;
      if (!isCleanLanding(defaultSort)) return;
      if (availInputs.some((input) => input.checked)) return;
      const facetChecked = Array.from(
        this.form.querySelectorAll('input[type="checkbox"]:checked')
      ).some((input) => !availInputs.includes(input));
      const sortChanged = this.sortInput && defaultSort && this.sortInput.value !== defaultSort;
      if (!facetChecked && !sortChanged) return;
      inStockInput.checked = true;
    });
  }

  /* /collections/all only (Liquid gates the endpoint). A designer with no
     piece in stock is hidden from the designer rail and from the FILTERS
     panel, even if sold-out pieces remain on catalog, and comes back by
     itself once a piece is available again. Source of truth: the store's
     own facet counts under the IN STOCK facet, served by the rs-vendors view
     as a few hundred bytes of JSON — one request per session (10 min
     cache). Skipped while OUT OF STOCK is being browsed, where those
     designers are the point; an active designer is never hidden, so it can
     always be removed. */
  async #setupVendorAvailability() {
    const endpoint = this.getAttribute('data-rs-vendors-endpoint');
    if (!endpoint) return;
    const name = this.getAttribute('data-rs-avail-name');
    const inStockValue = this.getAttribute('data-rs-avail-instock');
    const active = new URLSearchParams(location.search).getAll(name);
    if (active.some((value) => value !== inStockValue)) return;

    const vendors = await loadInStockVendors(endpoint);
    if (!vendors) return;
    const keep = new Set(vendors);

    for (const input of this.form.querySelectorAll(`input[name="${VENDOR_PARAM}"]`)) {
      if (keep.has(input.value) || input.checked) continue;
      const option = input.closest('.rs-filters__option');
      if (option) option.hidden = true;
    }

    if (!document.querySelector('rs-marquee-menu')) return;
    await customElements.whenDefined('rs-marquee-menu');
    for (const rail of document.querySelectorAll('rs-marquee-menu')) {
      rail.keepFacetValues?.(VENDOR_PARAM, keep);
    }
  }
}

const VENDOR_PARAM = 'filter.p.vendor';
const VENDORS_CACHE_KEY = 'rs-vendors-in-stock';
const VENDORS_CACHE_MS = 10 * 60 * 1000;

/* The in-stock vendor list, from the session cache when fresh, else from
   the rs-vendors view. Null when unknown: nothing gets hidden on doubt. */
async function loadInStockVendors(endpoint) {
  try {
    const cached = JSON.parse(sessionStorage.getItem(VENDORS_CACHE_KEY) || 'null');
    if (cached?.endpoint === endpoint && Date.now() - cached.at < VENDORS_CACHE_MS) return cached.vendors;
  } catch {
    // storage unavailable or corrupt: fetch
  }
  try {
    // No JSON Accept header: Shopify would answer a collection URL with its
    // own collection JSON instead of the view
    const response = await fetch(endpoint);
    if (!response.ok) return null;
    const { vendors } = await response.json();
    if (!Array.isArray(vendors)) return null;
    try {
      sessionStorage.setItem(VENDORS_CACHE_KEY, JSON.stringify({ endpoint, at: Date.now(), vendors }));
    } catch {
      // storage unavailable: fine, fetched again next time
    }
    return vendors;
  } catch {
    return null;
  }
}

if (!customElements.get('rs-filters')) {
  customElements.define('rs-filters', RsFilters);
}
